import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  TextField
} from '@mui/material';
import { Campaign as MicrosoftIcon } from '@mui/icons-material';
import api from '../../config/api';
import { brandService } from '../../services/brandService';

const MicrosoftAdsSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Multi-brand support
  const [brands, setBrands] = useState([]);
  const [accountBrandMapping, setAccountBrandMapping] = useState({});
  const [accountNameMapping, setAccountNameMapping] = useState({});
  const [defaultBrandId, setDefaultBrandId] = useState(null);

  // Parse URL parameters - Microsoft Ads uses temp_id from OAuth callback
  const urlParams = new URLSearchParams(location.search);
  const tempId = urlParams.get('temp_id');

  useEffect(() => {
    if (!tempId) {
      setError('Missing temporary ID. Please restart the OAuth process.');
      setLoading(false);
      return;
    }

    fetchAccounts();
    fetchBrands();
  }, [tempId]);

  const fetchBrands = async () => {
    try {
      const brandsData = await brandService.getBrands();
      setBrands(brandsData || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/api/connectors/microsoft-ads/accounts', {
        params: { temp_id: tempId }
      });

      if (response.data.success) {
        setAccounts(response.data.customer_accounts || []);
        setUserInfo(response.data.user_info);
        setDefaultBrandId(response.data.brand_id);

        // Initialize name mapping with account names
        const nameMapping = {};
        (response.data.customer_accounts || []).forEach(account => {
          nameMapping[account.id] = `Microsoft Ads - ${account.name}`;
        });
        setAccountNameMapping(nameMapping);
      } else {
        setError(response.data.message || 'Failed to fetch accounts');
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError(err.response?.data?.message || 'Failed to fetch Microsoft Ads accounts. The OAuth session may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelection = (accountId, checked) => {
    if (checked) {
      const account = accounts.find(a => a.id === accountId);
      setSelectedAccounts([...selectedAccounts, account]);
      // For already-connected accounts, default to their existing brand (re-auth flow).
      // Otherwise use the brand from the OAuth flow.
      const defaultBrand = account?.alreadyConnected && account?.connectedBrandId
        ? String(account.connectedBrandId)
        : defaultBrandId;
      setAccountBrandMapping(prev => ({
        ...prev,
        [accountId]: defaultBrand
      }));
    } else {
      setSelectedAccounts(selectedAccounts.filter(a => a.id !== accountId));
      // Remove brand mapping
      setAccountBrandMapping(prev => {
        const newMapping = { ...prev };
        delete newMapping[accountId];
        return newMapping;
      });
    }
  };

  const handleBrandChange = (accountId, newBrandId) => {
    setAccountBrandMapping(prev => ({
      ...prev,
      [accountId]: newBrandId
    }));
  };

  const handleNameChange = (accountId, newName) => {
    setAccountNameMapping(prev => ({
      ...prev,
      [accountId]: newName
    }));
  };

  const handleCreateConnectors = async () => {
    if (selectedAccounts.length === 0) {
      setError('Please select at least one account to connect');
      return;
    }

    // Validate brands
    const accountsWithoutBrands = selectedAccounts.filter(account =>
      !accountBrandMapping[account.id]
    );
    if (accountsWithoutBrands.length > 0) {
      setError('Please select a brand for all selected accounts');
      return;
    }

    setCreating(true);
    setError('');
    setSuccess('');

    try {
      const createdConnectors = [];

      for (const account of selectedAccounts) {
        const selectedBrandId = accountBrandMapping[account.id] || defaultBrandId;
        const connectorName = accountNameMapping[account.id] || `Microsoft Ads - ${account.name}`;

        const response = await api.post('/api/connectors/microsoft-ads/create', {
          temp_id: tempId,
          account_id: account.id,
          account_name: account.name,
          connector_name: connectorName,
          brand_id: parseInt(selectedBrandId)
        });

        if (response.data.success) {
          createdConnectors.push(response.data.connector);
        } else {
          throw new Error(`Failed to create connector for ${account.name}: ${response.data.message}`);
        }
      }

      setSuccess(`Successfully created ${createdConnectors.length} Microsoft Ads connector(s)!`);

      // Navigate back to connectors page after short delay
      setTimeout(() => {
        navigate('/connectors', {
          state: {
            message: `Successfully connected ${createdConnectors.length} Microsoft Ads account(s)`,
            connectors: createdConnectors
          }
        });
      }, 2000);

    } catch (err) {
      console.error('Error creating connectors:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create Microsoft Ads connectors');
    } finally {
      setCreating(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedAccounts.length === accounts.length) {
      setSelectedAccounts([]);
      setAccountBrandMapping({});
    } else {
      setSelectedAccounts([...accounts]);
      const mapping = {};
      accounts.forEach(account => {
        mapping[account.id] = account.alreadyConnected && account.connectedBrandId
          ? String(account.connectedBrandId)
          : defaultBrandId;
      });
      setAccountBrandMapping(mapping);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
        <CircularProgress size={48} />
        <Typography sx={{ mt: 2 }}>Loading Microsoft Ads accounts...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <MicrosoftIcon sx={{ fontSize: 40, color: '#00A4EF', mr: 2 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Select Microsoft Ads Accounts
              </Typography>
              {userInfo && (
                <Typography variant="body2" color="text.secondary">
                  Authenticated as: {userInfo.displayName || userInfo.userPrincipalName || 'Microsoft User'}
                </Typography>
              )}
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {accounts.length === 0 ? (
            <Alert severity="info">
              No Microsoft Ads accounts found for this user. Make sure you have access to at least one Microsoft Advertising account.
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">
                  Found {accounts.length} account(s)
                </Typography>
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  variant="outlined"
                >
                  {selectedAccounts.length === accounts.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {accounts.map((account) => {
                  const isSelected = selectedAccounts.some(a => a.id === account.id);
                  return (
                    <Grid item xs={12} key={account.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          p: 2,
                          border: isSelected ? '2px solid #00A4EF' : '1px solid #e0e0e0',
                          backgroundColor: isSelected ? 'rgba(0, 164, 239, 0.05)' : 'white'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => handleAccountSelection(account.id, e.target.checked)}
                                color="primary"
                              />
                            }
                            label=""
                            sx={{ m: 0 }}
                          />

                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {account.name}
                              </Typography>
                              <Chip
                                size="small"
                                label={account.status || 'Active'}
                                color={account.status === 'Active' ? 'success' : 'default'}
                              />
                            </Box>

                            <Typography variant="body2" color="text.secondary">
                              Account ID: {account.id}
                              {account.number && ` | Number: ${account.number}`}
                              {account.currency && ` | Currency: ${account.currency}`}
                              {account.timeZone && ` | Timezone: ${account.timeZone}`}
                            </Typography>
                            {account.alreadyConnected && (
                              <Box sx={{ mt: 0.5 }}>
                                <Chip
                                  label={account.connectedBrandName
                                    ? `Connected · ${account.connectedBrandName}`
                                    : 'Already connected'}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              </Box>
                            )}

                            {isSelected && (
                              <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <TextField
                                  size="small"
                                  label="Connector Name"
                                  value={accountNameMapping[account.id] || ''}
                                  onChange={(e) => handleNameChange(account.id, e.target.value)}
                                  sx={{ minWidth: 250 }}
                                />

                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                  <InputLabel>Brand</InputLabel>
                                  <Select
                                    value={accountBrandMapping[account.id] || ''}
                                    onChange={(e) => handleBrandChange(account.id, e.target.value)}
                                    label="Brand"
                                  >
                                    {brands.map((brand) => (
                                      <MenuItem key={brand.id} value={brand.id}>
                                        {brand.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/connectors')}
                  disabled={creating}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateConnectors}
                  disabled={selectedAccounts.length === 0 || creating}
                  sx={{
                    backgroundColor: '#00A4EF',
                    '&:hover': { backgroundColor: '#0078D4' }
                  }}
                >
                  {creating ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                      Creating...
                    </>
                  ) : (
                    `Connect ${selectedAccounts.length} Account${selectedAccounts.length !== 1 ? 's' : ''}`
                  )}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MicrosoftAdsSelection;
